import React, { useState } from 'react';
import {
  HbrTable,
  HbrForm,
  HbrInput,
} from '@harbor/elements-react-standalone';
import { MoodleGalleryImage } from '../../../types/moodleEventBooking';
import { JOOMLA_BASE_URL } from '../../../hooks/useMoodleEventBooking';
import StbModal from '../../security-toolbox-ui/StbModal';
import StbButton from '../../security-toolbox-ui/StbButton';
import { renderErrorMessage } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import { asSingleValue } from '../../../support/utils';


type MoodleGalleryTableProps = {
  gallery: MoodleGalleryImage[];
  onChange: (gallery: MoodleGalleryImage[]) => void;
};

export function MoodleGalleryTable({ gallery, onChange }: MoodleGalleryTableProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<MoodleGalleryImage>>({});
  const [urlTouched, setUrlTouched] = useState(false);
  const [tempId, setTempId] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);

  // Table columns
  const columns = [
    { prop: 'id', name: 'ID', sortable: true, size: 40 },
    { prop: 'title', name: 'Title', sortable: true, size: 180 },
    { prop: 'url', name: 'Image', sortable: false, size: 150,
      render: (row: MoodleGalleryImage) => (
        <img src={JOOMLA_BASE_URL + row.url} alt={row.caption ?? ''} style={{ maxWidth: 100, maxHeight: 80, objectFit: 'contain' }} />
      ),
    },
    // Add more columns if needed
  ];

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Add or edit image
  const handleSubmit = async () => {
    // e.preventDefault();
    if (!form.url) return;

    if (isEditing && form.id != null) {
      // Update existing
      onChange(gallery.map(img => img.id === form.id ? { ...img, ...form } as MoodleGalleryImage : img));
    } else {
      // Add new
      onChange([
        ...gallery,
        { ...form, id: tempId } as MoodleGalleryImage,
      ]);
      setTempId(id => id - 1);
    }
  };

  // Open modal with item for editing
  const handleRowClick = (row: MoodleGalleryImage) => {
    setForm(row);
    setModalOpen(true);
    setIsEditing(true);
  };

  // Open modal for new item
  const handleAdd = () => {
    setForm({});
    setModalOpen(true);
    setIsEditing(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ flex: 1, margin: 0 }}>Gallery</h3>
        <StbButton
          onClick={handleAdd}
          role="add"
          variant="text"
          size="medium"
          className="mt-3"
        >
          Add Gallery Image
        </StbButton>
      </div>
      {columns.length > 0 && gallery.length > 0 ? (
        <HbrTable
          columns={columns}
          useClipboard={false}
          headerSlot={true}
          source={gallery}
          canFocus={false}
          style={{ height: '300px', width: '100%' }}
          onSelect={handleRowClick}
        />
      ) : (
        <div className="text-sm italic text-gray-500 mt-2">No gallery images.</div>
      )}
      <StbModal
        open={modalOpen}
        onClose={async () => {
          setModalOpen(false);
          setIsEditing(false);
          setForm({});
          setUrlTouched(false);
        }}
        title={isEditing ? "Edit Gallery Image" : "Add Gallery Image"}
        submitLabel={isEditing ? "Update" : "Add"}
        submitDisabled={!form.url || form.url.trim() === ''}
        role={isEditing ? "update" : "save"}
        onSubmit={handleSubmit}
      >
        <HbrForm onSubmit={handleSubmit}>
          <HbrInput
            required
            name="url"
            value={form.url ?? ''}
            onHbr-input={(e) => {
              setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }));
              setUrlTouched(true);
            }}
          >
            <div slot="label">Image URL <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(urlTouched && (!form.url || form.url.trim() === ''), "Image URL is required")}
          </HbrInput>
          <HbrInput
            name="caption"
            label="Caption"
            value={form.caption ?? ''}
            onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))}
          />
        </HbrForm>
      </StbModal>
    </div>
  );
}